import { getProjectApiClient } from "@api/project-api/project-api-utils";
import { FaroSwitch } from "@components/common/faro-switch";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useToast } from "@flat-ui";
import { FeatureSettingLayout, TOOLTIP_TEXT_CHECKED, TOOLTIP_TEXT_NOT_CHECKED } from "@src/pages/project-details/project-settings/feature-setting-layout";
import { fetchingProjectsFlagsSelector, isSelectedProjectEditableSelector, selectedProjectSelector, shouldDisplayMarkupsForViewersSelector } from "@store/projects/projects-selector";
import { updateProjectSettings } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { isProjectDemo } from "@utils/project-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useMemo } from "react";

/** Setting to display annotations for users with viewer roles */
export function SettingDisplayForViewers() {
  const {
    openToast
  } = useToast();
  const selectedProject = useAppSelector(selectedProjectSelector);
  const isChecked = useAppSelector(shouldDisplayMarkupsForViewersSelector);
  const {
    isUpdatingProjectSettings,
    isFetchingProjectSettings
  } = useAppSelector(fetchingProjectsFlagsSelector);
  const isProjectEditable = useAppSelector(isSelectedProjectEditableSelector());
  const {
    canEditProjectSettings
  } = useHasUserValidPermissionProjectLevel();
  const {
    trackEvent
  } = useTrackEvent();
  const dispatch = useAppDispatch();
  const isDisabled = useMemo(() => {
    return !isProjectEditable || !canEditProjectSettings || !selectedProject || isProjectDemo(selectedProject) || isFetchingProjectSettings || isUpdatingProjectSettings;
  }, [canEditProjectSettings, isFetchingProjectSettings, isProjectEditable, isUpdatingProjectSettings, selectedProject]);
  const onChange = useCallback((_, shouldCheck) => {
    trackEvent({
      name: ProjectEvents.updateProjectSettings,
      props: {
        setting: "display-annotations-for-viewers",
        isSettingEnabled: shouldCheck
      }
    });
    try {
      if (!selectedProject) {
        throw new Error("Selected project is undefined");
      }
      const projectApiClient = getProjectApiClient({
        projectId: selectedProject.id
      });
      void dispatch(updateProjectSettings({
        projectApiClient,
        payload: {
          shouldDisplayMarkupsForViewers: shouldCheck
        }
      }));
    } catch (error) {
      openToast({
        title: "Cannot update project settings",
        message: getErrorDisplayMarkup(error),
        variant: "error"
      });
    }
  }, [dispatch, selectedProject, openToast, trackEvent]);
  return <FeatureSettingLayout featureName="Project viewers" actionComponent={<FaroSwitch onChange={onChange} isLoading={isUpdatingProjectSettings} disabled={isDisabled} checked={isChecked} tooltipTextChecked={TOOLTIP_TEXT_CHECKED} tooltipTextNotChecked={TOOLTIP_TEXT_NOT_CHECKED} sx={{
    cursor: isDisabled ? "not-allowed" : "pointer"
  }} />} />;
}